import React, { useEffect, useMemo, useState } from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { CPDropdown } from "../CPDropdown";
import { CPTextarea } from "../CPTextarea";
import "./adminDesktop.css";
import { useOrganization } from "../../context/OrganizationContext";
import {
  ORG_PAYMENT_TERMS_OPTIONS,
  buildPaymentTermsPresentation,
  buildPaymentTermsState,
  getOrganizationPaymentTermsConfig,
  isManualDueDatePreset,
  todayIsoDate,
} from "../../helpers/invoicePaymentTerms";
import {
  EMPTY_PAYMENT_INSTRUCTIONS,
  normalizePaymentInstructions,
  validatePaymentInstructions,
} from "../../helpers/invoicePaymentInstructions";
import { normalizeCustomerNotes } from "../../helpers/invoiceCustomerNotes";
import {
  buildInvoiceFooterPresentation,
  normalizeInvoiceFooter,
} from "../../helpers/invoiceFooter";
import { PaymentInstructionsFields } from "../invoice/PaymentInstructionsFields";
import { PaymentInstructionsLivePreview } from "../invoice/PaymentInstructionsLivePreview";
import { listFinanceAccounts } from "../../helpers/financeAccounts";
import { supabase } from "../../supabase";
import { isFinanceModuleEnabled } from "../../helpers/nexoraOpsAccess";

function CommercialSectionPreview({ presentation, emptyLabel }) {
  if (!presentation?.blocks?.length) {
    return (
      <div
        style={{
          marginTop: "16px",
          padding: "14px 16px",
          borderRadius: "10px",
          border: "1px dashed rgba(255,255,255,0.18)",
          background: "rgba(0,0,0,0.18)",
        }}
      >
        <div
          style={{
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            opacity: 0.55,
            marginBottom: "8px",
          }}
        >
          Invoice preview
        </div>
        <p style={{ margin: 0, fontSize: "13px", opacity: 0.55 }}>
          {emptyLabel}
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: "16px",
        padding: "14px 16px",
        borderRadius: "10px",
        border: "1px solid rgba(201,168,76,0.28)",
        background: "rgba(255,255,255,0.03)",
      }}
      aria-live="polite"
    >
      <div
        style={{
          fontSize: "11px",
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          opacity: 0.55,
          marginBottom: "10px",
        }}
      >
        Invoice preview
      </div>
      {presentation.title ? (
        <div
          style={{
            fontWeight: 700,
            fontSize: "14px",
            marginBottom: "12px",
            color: "#c9a84c",
          }}
        >
          {presentation.title}
        </div>
      ) : null}
      {presentation.blocks.map((block, index) => {
        if (block.type === "field") {
          return (
            <div key={`field-${index}`} style={{ marginBottom: "10px" }}>
              <div
                style={{ fontSize: "12px", opacity: 0.6, marginBottom: "3px" }}
              >
                {block.label}
              </div>
              <div style={{ fontSize: "14px", fontWeight: 600 }}>
                {block.value}
              </div>
            </div>
          );
        }
        if (block.type === "text") {
          return (
            <p
              key={`text-${index}`}
              style={{
                margin: "0 0 8px",
                fontSize: "13px",
                lineHeight: 1.45,
                opacity: 0.85,
                whiteSpace: "pre-wrap",
              }}
            >
              {block.text}
            </p>
          );
        }
        return null;
      })}
    </div>
  );
}

export function AdminInvoicesSettingsScreen({ onBack }) {
  const { settings, updateSettings, loading, error } = useOrganization();
  const [paymentTermsPreset, setPaymentTermsPreset] = useState("net_30");
  const [customerNotes, setCustomerNotes] = useState("");
  const [invoiceFooter, setInvoiceFooter] = useState("");
  const [paymentInstructions, setPaymentInstructions] = useState(() => ({
    ...EMPTY_PAYMENT_INSTRUCTIONS,
  }));
  const [fieldErrors, setFieldErrors] = useState({});
  const [depositAccountId, setDepositAccountId] = useState("");
  const [financeAccounts, setFinanceAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const financeEnabled = isFinanceModuleEnabled(settings);

  useEffect(() => {
    const terms = getOrganizationPaymentTermsConfig(settings);
    setPaymentTermsPreset(terms.preset);
    setCustomerNotes(
      normalizeCustomerNotes(settings.invoices?.default_customer_notes),
    );
    setInvoiceFooter(
      normalizeInvoiceFooter(settings.invoices?.default_invoice_footer),
    );
    setPaymentInstructions(
      normalizePaymentInstructions(settings.invoices?.payment_instructions),
    );
    setDepositAccountId(
      settings.modules?.finance?.default_invoice_deposit_account_id || "",
    );
  }, [settings]);

  useEffect(() => {
    if (!financeEnabled) {
      setFinanceAccounts([]);
      return undefined;
    }

    let cancelled = false;
    setAccountsLoading(true);
    void listFinanceAccounts(supabase, { includeInactive: false })
      .then((rows) => {
        if (!cancelled) {
          setFinanceAccounts(rows || []);
        }
      })
      .catch((err) => {
        console.error("[Admin] load finance accounts failed:", err);
        if (!cancelled) {
          setFinanceAccounts([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setAccountsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [financeEnabled]);

  const paymentTermsPreview = useMemo(() => {
    const sample = buildPaymentTermsState({
      preset: paymentTermsPreset,
      invoiceDate: todayIsoDate(),
      dueDate: isManualDueDatePreset(paymentTermsPreset)
        ? todayIsoDate()
        : null,
      previousDays: null,
    });
    return buildPaymentTermsPresentation({
      payment_terms_preset: sample.payment_terms_preset,
      payment_terms_days: sample.payment_terms_days,
      invoice_date: sample.invoice_date,
      due_date: sample.due_date,
    });
  }, [paymentTermsPreset]);

  const customerNotesPreview = useMemo(() => {
    const text = normalizeCustomerNotes(customerNotes);
    if (!text) return null;
    return {
      title: "Customer Notes",
      blocks: [{ type: "text", text }],
    };
  }, [customerNotes]);

  const invoiceFooterPreview = useMemo(
    () =>
      buildInvoiceFooterPresentation(invoiceFooter, {
        includeAdminTitle: true,
      }),
    [invoiceFooter],
  );

  const handleSave = async () => {
    const validation = validatePaymentInstructions(paymentInstructions);
    setFieldErrors(validation.errors);
    if (!validation.valid) {
      return;
    }

    const termsState = buildPaymentTermsState({
      preset: paymentTermsPreset,
      invoiceDate: todayIsoDate(),
      dueDate: null,
      previousDays: null,
    });

    setSaving(true);
    try {
      const patch = {
        invoices: {
          default_payment_terms_preset: termsState.payment_terms_preset,
          default_payment_terms_days:
            termsState.payment_terms_days ?? 30,
          default_customer_notes: normalizeCustomerNotes(customerNotes),
          default_invoice_footer: normalizeInvoiceFooter(invoiceFooter),
          payment_instructions: validation.config,
        },
      };

      if (financeEnabled) {
        patch.modules = {
          finance: {
            ...(settings.modules?.finance || {}),
            enabled: settings.modules?.finance?.enabled === true,
            manager_access: settings.modules?.finance?.manager_access === true,
            default_invoice_deposit_account_id: depositAccountId || null,
          },
        };
      }

      await updateSettings(patch);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <CrewPilotModuleShell
      title="Invoice Settings"
      subtitle="Default payment terms, how to pay, notes, footer, and Finance deposit account"
      sectionLabel="Billing & Documents"
      onBack={onBack}
      layout={PAGE_LAYOUT.FORM}
    >
      <div className="adm-split">
      <div className="rb-card">
        <div className="section-heading">PAYMENT TERMS</div>

        {loading && (
          <p className="admin-placeholder-note" style={{ marginBottom: "16px" }}>
            Loading invoice settings…
          </p>
        )}

        {error && (
          <p className="admin-placeholder-note" style={{ marginBottom: "16px" }}>
            Database sync unavailable. Changes are cached locally until online.
          </p>
        )}

        <p className="admin-placeholder-note" style={{ marginBottom: "16px" }}>
          New invoices inherit these terms. Due date and net days remain the
          Accounts Receivable source of truth for aging and reminders.
          {isManualDueDatePreset(paymentTermsPreset)
            ? " Specific-date and custom terms let each invoice choose its due date."
            : ""}
        </p>

        <CPDropdown
          label="Default payment terms"
          value={paymentTermsPreset}
          onChange={setPaymentTermsPreset}
          includePlaceholder={false}
          options={ORG_PAYMENT_TERMS_OPTIONS}
        />

        <CommercialSectionPreview
          presentation={paymentTermsPreview}
          emptyLabel="Payment terms preview unavailable for this selection."
        />
      </div>

      <div className="rb-card" style={{ marginTop: "16px" }}>
        <div className="section-heading">PAYMENT INSTRUCTIONS</div>
        <p className="admin-placeholder-note" style={{ marginBottom: "16px" }}>
          Shown on invoice Preview and PDF under “How to Pay”. New drafts inherit
          these defaults; each invoice can override them before send. Leave blank
          to omit the section.
        </p>

        <PaymentInstructionsFields
          variant="admin"
          value={paymentInstructions}
          onChange={(next) => {
            setPaymentInstructions(next);
            setFieldErrors({});
          }}
          errors={fieldErrors}
          disabled={saving}
        />

        <PaymentInstructionsLivePreview
          variant="admin"
          value={paymentInstructions}
        />
      </div>

      <div className="rb-card" style={{ marginTop: "16px" }}>
        <div className="section-heading">CUSTOMER NOTES</div>
        <p className="admin-placeholder-note" style={{ marginBottom: "16px" }}>
          Optional closing note on the customer invoice (after Payment Terms).
          New drafts inherit this text; each invoice can override it. Leave blank
          to omit the section.
        </p>

        <CPTextarea
          label="Default customer notes"
          value={customerNotes}
          onChange={setCustomerNotes}
          rows={4}
          placeholder="Thank you for choosing us. Please contact us with any questions."
          disabled={saving}
        />

        <CommercialSectionPreview
          presentation={customerNotesPreview}
          emptyLabel="No customer notes configured — this section will be omitted from the invoice."
        />
      </div>

      <div className="rb-card" style={{ marginTop: "16px" }}>
        <div className="section-heading">INVOICE FOOTER</div>
        <p className="admin-placeholder-note" style={{ marginBottom: "16px" }}>
          Optional closing text at the very bottom of the customer invoice
          (after Customer Notes). Use for billing contacts, legal disclaimers,
          or other closing information. Leave blank to omit.
        </p>

        <CPTextarea
          label="Default invoice footer"
          value={invoiceFooter}
          onChange={setInvoiceFooter}
          rows={4}
          placeholder={
            "Questions about this invoice?\nContact us at info@example.com or 555-0100."
          }
          disabled={saving}
        />

        <CommercialSectionPreview
          presentation={invoiceFooterPreview}
          emptyLabel="No invoice footer configured — this closing section will be omitted."
        />
      </div>

      {financeEnabled ? (
        <div className="rb-card" style={{ marginTop: "16px" }}>
          <div className="section-heading">FINANCE</div>
          <p className="admin-placeholder-note" style={{ marginBottom: "16px" }}>
            Default Invoice Deposit Account — where payment for new invoices is
            expected to land. New invoices inherit this account; each invoice can
            still be changed before it is paid.
          </p>

          <CPDropdown
            label="Default Invoice Deposit Account"
            value={depositAccountId}
            onChange={setDepositAccountId}
            disabled={accountsLoading}
            options={[
              {
                value: "",
                label: accountsLoading
                  ? "Loading accounts…"
                  : "Select account",
              },
              ...financeAccounts.map((account) => ({
                value: account.id,
                label: account.name,
              })),
            ]}
          />
        </div>
      ) : null}

      <div className="adm-span adm-actions" style={{ marginTop: "16px" }}>
        <button
          type="button"
          className="kpi-back-btn adm-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save invoice settings"}
        </button>

        {saved && (
          <p className="admin-save-confirmation" role="status">
            Invoice settings updated.
          </p>
        )}
      </div>
      </div>
    </CrewPilotModuleShell>
  );
}
